import { getStatus, isOperationOutcome } from '@medplum/core';

/**
 * Read-side counterpart of the renderer's classifyWriteError: collapses a
 * failed FHIR read into the two outcomes the UI degrades on. Unlike writes
 * (which fail loudly with a four-way taxonomy), a read only needs to tell
 * "the server withheld this on purpose" apart from "something broke".
 */
export type FhirReadError = {
  kind: 'forbidden' | 'error';
  message: string;
};

/** Shapes Apollo attaches to a failed query, checked structurally so the
 * classifier accepts any unknown error without an @apollo/client instanceof. */
type ApolloErrorShape = {
  graphQLErrors?: readonly { message?: unknown }[];
  networkError?: { statusCode?: unknown } | null;
};

/**
 * Classify a failed FHIR read (GraphQL or REST) as forbidden vs error.
 *
 * Medplum surfaces an AccessPolicy denial differently per transport:
 * - REST: the SDK throws OperationOutcomeError; the outcome's issue code maps
 *   to HTTP 403 via getStatus (Orchard's isForbiddenError precedent).
 * - GraphQL, denied at the HTTP layer: Apollo networkError with statusCode 403.
 * - GraphQL, denied per-field by a resolver: HTTP 200 whose errors[] entries
 *   carry the forbidden outcome's message ("Forbidden") — Medplum's
 *   fhir-router returns the raw execute() result with no error extensions,
 *   so the message text is the only signal on the wire.
 *
 * 401 stays 'error': it means the session/token broke (the FHIR Apollo
 * client's onUnauthorized path re-initializes), not that access was denied.
 */
export function classifyReadError(error: unknown): FhirReadError {
  const message = error instanceof Error ? error.message : String(error);
  return { kind: isForbidden(error) ? 'forbidden' : 'error', message };
}

function isForbidden(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;

  const outcome = (error as { outcome?: unknown }).outcome;
  if (isOperationOutcome(outcome)) return getStatus(outcome) === 403;

  const { graphQLErrors, networkError } = error as ApolloErrorShape;
  if (networkError?.statusCode === 403) return true;
  if (
    graphQLErrors?.some(
      (graphQLError) =>
        typeof graphQLError.message === 'string' &&
        /forbidden/i.test(graphQLError.message),
    )
  ) {
    return true;
  }

  const status =
    (error as { response?: { status?: number } }).response?.status ??
    (error as { status?: number }).status;
  return status === 403;
}
